/**
 * Centralized build-time feature flags.
 *
 * Flags are injected from `REACT_APP_*` env vars (CRA reads them
 * automatically; RSBuild requires each one to be listed in the `define`
 * block of `rsbuild.config.ts`). Keep every flag read here so menu, routes
 * and components agree on the same value.
 */
import { isProductionEnvironment } from "@shared/navigation/SideMenu/sideMenuEnv";

/**
 * Tags Manager (centralized Tags + TaggedResources pivot).
 *
 * When disabled, the Tags Manager page/menu entry and the rich resource tag
 * picker are hidden and the legacy free-text tag flow is used instead, so the
 * new tagging can coexist with the legacy one. Mirrors the backend
 * `TAGS_MANAGER_ENABLED` flag.
 *
 * Resolution order (so it can be controlled WITHOUT Bitbucket access):
 *   1. Explicit override — `REACT_APP_ENABLE_TAGS_MANAGER=true|false` always
 *      wins (kill-switch / force-on, set in `.env.local` or a deployment var).
 *   2. No flag set → enabled in every NON-production build (local, dev,
 *      staging / `develop`) and disabled in production. This keys off
 *      `REACT_APP_ENV` (which the pipeline sets reliably: `staging` for
 *      `develop`, `production` for prod builds), so the feature ships to
 *      develop/staging by default while prod stays hidden until an explicit
 *      cutover — same pattern other beta features use via
 *      `isProductionEnvironment()`.
 */
export const isTagsManagerEnabled = (): boolean => {
	const raw = (process.env.REACT_APP_ENABLE_TAGS_MANAGER || "")
		.trim()
		.toLowerCase();
	if (raw === "true") return true;
	if (raw === "false") return false;
	return !isProductionEnvironment();
};

/**
 * Subscriptions management (Account → Subscriptions).
 *
 * WIP (CTX-26): the internal subscription model replaced Chargebee as the
 * source of truth, but there is no UI to administer terms and renewals yet.
 * The section ships hidden in production while it is being built and its
 * backing API (CTX-208) lands, and is visible in every NON-production build
 * (local, dev, staging / `develop`).
 *
 * Resolution order mirrors {@link isTagsManagerEnabled} so the rollout can be
 * driven WITHOUT Bitbucket access:
 *   1. Explicit override — `REACT_APP_ENABLE_SUBSCRIPTIONS=true|false` always
 *      wins (kill-switch / force-on, set in `.env.local` or a deployment var).
 *      Remember to register it in `rsbuild.config.ts` `define`, otherwise it
 *      never reaches the bundle.
 *   2. No flag set → enabled outside production, disabled in production.
 *
 * This flag answers "does the feature exist here?". WHO may use it is a
 * separate question owned by `getSubscriptionsAllowedTenantTypes()`.
 *
 * @param isProduction Environment to evaluate against. Callers that already
 * carry the environment — `filterAccountAreaItems` receives it as a
 * parameter — should pass it instead of letting this resolve it again, so the
 * rule can be exercised in tests. `REACT_APP_*` reads are inlined at build
 * time by `rsbuild.config.ts`, which makes the default path impossible to vary
 * at runtime.
 */
export const isSubscriptionsManagerEnabled = (
	isProduction: boolean = isProductionEnvironment()
): boolean => {
	const raw = (process.env.REACT_APP_ENABLE_SUBSCRIPTIONS || "")
		.trim()
		.toLowerCase();
	if (raw === "true") return true;
	if (raw === "false") return false;
	return !isProduction;
};

/**
 * Release Notes / Changelog (`/changelog`).
 *
 * **GA since CTX-616.** It was `!isProductionEnvironment()` from the first
 * commit until the cutover: built, ingested and rendered everywhere except the
 * one environment with customers in it.
 *
 * Whether the feature exists is now settled; **who sees it is not**, and that
 * is {@link canSeeChangelog}'s job — in production the feed is still
 * `GLOBAL_ADMIN` only (CTX-521). Turning this on did not open the feed to
 * anyone new; it made the admin-only rollout reachable at all.
 *
 * ── Why no `REACT_APP_ENABLE_CHANGELOG` kill-switch ─────────────────────────
 *
 * Because it would not be one. `REACT_APP_*` is inlined into the bundle at
 * **build time** by `source.define`, so flipping the variable costs a rebuild
 * and a redeploy — the exact cost of reverting this line. It would buy nothing
 * operationally while adding a `source.define` entry, a `verify-env-bundle.js`
 * manifest to keep in step, and the migration's most common failure: a flag
 * that silently reads `undefined` because someone added it in one place only.
 *
 * Kept as a function rather than inlined at its call site so the decision still
 * lives in one place. Switching the feed off is editing this return value.
 */
export const isChangelogEnabled = (): boolean => true;

/**
 * Who may see the changelog, on top of whether it exists at all.
 *
 * Source of truth for every changelog surface. There are six — the
 * `/changelog` routes, the footer chip, the What's New card, the modal that
 * card opens, the "New" tags on menu items, and the walkthrough tour the modal
 * launches (`WalkthroughTourProvider`, CTX-510) — and four of them arrived
 * within a week of each other. A gate applied per surface would have been
 * wrong by the next feature, so all of them go through here.
 *
 * The tour is the one that reads least like a changelog surface: it runs on a
 * product screen, not on changelog furniture. It is one anyway — it exists
 * only because an entry announced it, and gating it any other way would let a
 * tenant walk through a change the other five hide from them.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 *
 * Outside production, unchanged: everyone who can see the feature still sees
 * it. That is deliberate — it is the only way to check what a PARTNER or a
 * CUSTOMER actually gets, and moving the restriction into staging would make
 * that untestable.
 *
 * In production, `GLOBAL_ADMIN` only. Not an entitlement rule: it is a rollout
 * gate, so the first weeks of the feed in front of real tenants are watched by
 * the people who can fix it. The entitlement rule — products × audiences — is
 * the server's and stays there (CTX-199, CTX-202).
 *
 * ── What this is not ────────────────────────────────────────────────────────
 *
 * Not a security control. `GET /api/v2/changelog` filters by product and
 * audience, never by tenant type, so a PARTNER calling it directly still gets
 * their entries. That is fine — the content is customer-facing copy they are
 * meant to read shortly — but nobody should later mistake this for a boundary.
 *
 * ── This is now load-bearing ────────────────────────────────────────────────
 *
 * It was written before it could do anything: `isChangelogEnabled()` was false
 * in production, so the tenant-type branch was unreachable. That was the point
 * — the gate had to exist *before* the cutover, or the cutover would have been
 * the moment every tenant met an unwatched feed.
 *
 * Since CTX-616 the cutover has happened and this clause is the only thing
 * keeping the production feed to admins. Deleting it opens the feed to every
 * tenant type, so that deletion is its own change, with evidence behind it
 * (CTX-736) rather than a tidy-up.
 */
export const canSeeChangelog = (tenantType: string | undefined): boolean => {
	if (!isChangelogEnabled()) return false;
	return !isProductionEnvironment() || tenantType === "GLOBAL_ADMIN";
};

/**
 * Customer API tokens (Account → API, `/account/api`).
 *
 * WIP: the view was rebuilt around who holds a key and until when, and it is
 * still in progress — the page talks to a separate backend (`CUSTOMER_API_URL`,
 * not this dashboard's own API) whose deployment variable does not exist in the
 * production environments yet. Shipping it visible would put a page in front of
 * customers that cannot reach its API.
 *
 * Enabled in every NON-production build (local, dev, staging) and hidden in
 * production until an explicit cutover — same pattern as
 * {@link isExtenderPopEnabled}. (`isInternetMonitoringEnabled` used to be the
 * other example; it went GA at the CTX-339 cutover.)
 *
 * This is the single predicate behind BOTH surfaces the CTX-36 rule names: the
 * sidemenu row (`filterAccountAreaItems`, on the
 * `account-management-integrations` id) and the route registration in
 * `src/settings/routes.tsx`. They were two separate expressions of the same
 * rule, which is how `/account/integrations` stayed reachable by direct URL in
 * production while its menu entry was hidden (CTX-37).
 *
 * @param isProduction Environment to evaluate against. Callers that already
 * carry the environment — `filterAccountAreaItems` receives it as a
 * parameter — should pass it instead of letting this resolve it again, so the
 * rule can be exercised in tests. `REACT_APP_*` reads are inlined at build time
 * by `rsbuild.config.ts`, which makes the default path impossible to vary at
 * runtime.
 */
export const isCustomerApiEnabled = (
	isProduction: boolean = isProductionEnvironment()
): boolean => !isProduction;

/**
 * Network Topology (CTX-97) — the source-first
 * Reflector/Cluster -> Cloud -> Region -> Account topology view backed by
 * the CTX-96 `chopper-backend` `/api/v2/insight/*` contract. Lives under
 * Reflector v2.0 -> Management, after BGP Visibility.
 *
 * GA since 2026-08-06: the view ships to every environment, so the gate is a
 * constant rather than an env check. Kept as a named helper (instead of
 * deleting the call sites) so the sidemenu entry and both route trees stay
 * switchable from one place if it ever needs turning off in a hurry.
 */
export const isInsightTopologyV2Enabled = (): boolean => true;

/**
 * Internet monitoring (`/kontrol/internet-ip-monitoring`) — the IPSLA-monitored
 * IPs per backbone uplink, under Kontrol Edge -> Monitoring.
 *
 * GA since the CTX-339 cutover: the view ships to every environment, so this
 * is a constant rather than an env check. Kept as a named helper instead of
 * deleting the call sites, so the sidemenu row and the route stay switchable
 * from one place if it ever needs turning off in a hurry — same shape as
 * {@link isInsightTopologyV2Enabled}.
 *
 * **This was never the audience rule, and turning it on did not widen one.**
 * Who may open the page is decided by `canAccessKontrolAlerts`, which the row
 * and the route both share: CUSTOMER never, PARTNER and GLOBAL_ADMIN only
 * while standing in the partner's own view rather than one of its customers'.
 * That predicate is untouched here, and it is what makes the cutover
 * partner-only.
 *
 * It is also not the boundary. This runs in the browser, off state the
 * browser owns; the endpoint's own `alertsAccessControl` is what actually
 * refuses a request.
 */
export const isInternetMonitoringEnabled = (): boolean => true;

/**
 * Extended PoP (`/kontrol/extender-pop` + its detail view) — the per-router
 * customer site service (CTX-358..364).
 *
 * BETA: shown in every NON-production build (local, dev, staging) and hidden
 * in production until an explicit cutover — same pattern as
 * `isCustomerApiEnabled`. Gates the sidemenu row, both routes and the
 * Tags Manager wizard entry, so the feature is unreachable in prod even by
 * direct URL.
 */
export const isExtenderPopEnabled = (): boolean => !isProductionEnvironment();
